import type { Workspace } from "@/providers/workspace-provider"

export function normalizeContentRelativePath(path: string): string {
  return path.startsWith("/") ? path.slice(1) : path
}

/**
 * Abbreviation for small badges (e.g. "Hop Studios" → "HS", "Personal" → "P").
 */
export function abbreviateWorkspaceName(name: string): string {
  const cleaned = name.replace(/\([^)]*\)/g, " ").trim()
  const parts = cleaned.split(/\s+/).filter(Boolean)
  if (parts.length === 0) return "?"
  if (parts.length === 1) {
    const w = parts[0]
    return w[0]?.toUpperCase() ?? "?"
  }
  return parts
    .map((p) => p[0] ?? "")
    .filter((c) => /[A-Za-z0-9]/.test(c))
    .join("")
    .toUpperCase()
}

/** Longest matching contentRoot wins (most specific workspace). */
export function workspaceForFilePath(
  filePath: string | undefined,
  workspaces: Workspace[],
): Workspace | null {
  if (!filePath || workspaces.length === 0) return null
  const normalized = normalizeContentRelativePath(filePath)
  let best: { workspace: Workspace; rootLen: number } | null = null
  for (const w of workspaces) {
    if (!w.contentRoot) continue
    const root = w.contentRoot.replace(/\/+$/, "")
    if (normalized === root || normalized.startsWith(`${root}/`)) {
      if (!best || root.length > best.rootLen) {
        best = { workspace: w, rootLen: root.length }
      }
    }
  }
  return best?.workspace ?? null
}

export function workspaceBadgeFromPath(
  filePath: string | undefined,
  workspaces: Workspace[],
): { abbreviation: string; color: string | null; name: string } | null {
  const w = workspaceForFilePath(filePath, workspaces)
  if (!w) return null
  return {
    abbreviation: abbreviateWorkspaceName(w.name),
    color: w.color,
    name: w.name,
  }
}
